import { useForm, Controller } from 'react-hook-form';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import Heading from 'storybookConfig/stories/molecules/Heading';
import Input from 'storybookConfig/stories/molecules/Input';
import Form from 'storybookConfig/stories/organisms/Form';
import Flex from '@react-css/flex';
import TextAreaInput from 'storybookConfig/stories/molecules/Input/TextAreaInput';
import { useAppSelector, useAppDispatch } from 'store/index';
import { selectCompanyId } from 'store/selectors/me/company';
import { updateMarketplaceOnboarding } from 'store/slices/me';
import CategorySelector from './CategorySelector';
import { useMutation } from '@tanstack/react-query';
import { updateCompany } from 'utils/api/companies';
import useAlertQueue from 'hooks/useAlertQueue';
import TertiaryButton from 'storybookConfig/stories/molecules/Button/TertiaryButton';
import Toggle from 'storybookConfig/stories/molecules/Toggle';
import Body from 'storybookConfig/stories/molecules/Body';
import type { FormProps, MarketplaceFormData } from './types';
import useSegment from 'hooks/useSegment';
import InfoTooltip from 'components/Common/InfoTooltip';

type FormFields = Pick<
  MarketplaceFormData,
  | 'yearEstablished'
  | 'location'
  | 'brandDescription'
  | 'attributes'
  | 'primaryCategories'
  | 'secondaryCategories'
  | 'nsfwProducts'
>;

const BrandInfo = ({ onNext, onBack, marketplaceData, setMarketplaceData }: FormProps) => {
  const { addErrorAlert, addSuccessAlert } = useAlertQueue();
  const dispatch = useAppDispatch();
  const companyId = useAppSelector(selectCompanyId);

  const { control, handleSubmit, register, formState } = useForm<FormFields>({
    mode: 'onChange',
    values: marketplaceData,
  });
  const analytics = useSegment();

  const updatingCompany = useMutation({
    mutationFn: (data: FormFields) => {
      const updatedData = {
        yearEstablished: Number(data.yearEstablished),
        location: data.location,
        brandDescription: data.brandDescription,
        primaryCategories: data.primaryCategories,
        secondaryCategories: data.secondaryCategories,
        nsfwProducts: data.nsfwProducts,
      };
      setMarketplaceData(updatedData);
      return updateCompany(companyId ?? '', {
        marketplaceSettings: updatedData,
      });
    },
    onSuccess: (data) => {
      analytics?.track('Supplier Onboarding Brand Info Completed', {
        year_established: data.marketplaceSettings.yearEstablished,
        location: data.marketplaceSettings.location,
        brand_description: data.marketplaceSettings.brandDescription,
        primary_category: data.marketplaceSettings.primaryCategories,
        secondary_category: data.marketplaceSettings.secondaryCategories,
        nsfw_products: data.marketplaceSettings.nsfwProducts,
      });

      dispatch(updateMarketplaceOnboarding(data.marketplaceSettings));
      addSuccessAlert('Brand Info Updated', 'Your brand information has been saved.');
      if (onNext) {
        onNext();
      }
    },
    onError: (error: { message: string }) => {
      analytics?.track('Supplier Onboarding Step Error', {
        step_name: 'brand-info',
        error_message: error.message,
      });
      addErrorAlert('Something went wrong', error.message);
      console.error('Unable to update marketplace settings', error);
    },
  });

  return (
    <Form onSubmit={handleSubmit((data) => updatingCompany.mutate(data))}>
      <Flex>
        <TertiaryButton
          type="button"
          $removePadding="left"
          onClick={onBack}
          $iconName="navigate_before"
        >
          Back
        </TertiaryButton>
      </Flex>
      <Heading variant="Headings/H3">Brand Information</Heading>

      <Form.FieldGroup>
        <Flex gap="16px">
          <Form.Field
            labelText="Year Established"
            id="year-established"
            errorText={formState.errors.yearEstablished?.message}
            isRequired
          >
            <Input
              {...register('yearEstablished', {
                required: 'Year Established is required',
                pattern: {
                  value: /^\d{4}$/,
                  message: 'Please enter a valid 4-digit year',
                },
                validate: (value) => {
                  const year = Number(value);
                  const currentYear = new Date().getFullYear();
                  return year >= 1800 && year <= currentYear
                    ? true
                    : `Year must be between 1800 and ${currentYear}`;
                },
                onChange: (e) => {
                  const numericValue = e.target.value.replace(/\D/g, '').slice(0, 4);
                  setMarketplaceData({ yearEstablished: Number(numericValue) });
                },
              })}
              type="text"
              inputMode="numeric"
              pattern="\d*"
              placeholder="2021"
              isFullWidth
            />
          </Form.Field>
          <Form.Field
            labelText="Location"
            id="location"
            errorText={formState.errors.location?.message}
            isRequired
          >
            <Input
              {...register('location', {
                required: 'Location is required',
                onChange: (e) => {
                  const { value } = e.target;
                  setMarketplaceData({ location: value });
                },
              })}
              placeholder="San Diego, CA"
              isFullWidth
            />
          </Form.Field>
        </Flex>
        <Form.Field labelText="Brand Description" id="brand-description">
          <TextAreaInput
            as="textarea"
            {...register('brandDescription', {
              onChange: (e) => {
                const { value } = e.target;
                setMarketplaceData({ brandDescription: value });
              },
            })}
            placeholder="Give a brief description of your brand here"
            isFullWidth
          />
        </Form.Field>
        <Form.Field
          labelText={
            <Flex gap="4px" alignItemsCenter>
              Primary Categories
              <InfoTooltip>
                Choose the Primary Categories that best represent your brand&aps product offerings.
                This helps retailers discover you based on what you sell.
              </InfoTooltip>
            </Flex>
          }
          id="primary-categories"
        >
          <Controller
            control={control}
            name="primaryCategories"
            render={({ field }) => (
              <CategorySelector
                value={(field.value ?? []).filter((v): v is string => typeof v === 'string')}
                onChange={(categories) => {
                  field.onChange(categories);
                  setMarketplaceData({ primaryCategories: categories });
                }}
                label="Primary Categories"
              />
            )}
          />
        </Form.Field>
        <Form.Field
          labelText={
            <Flex gap="4px" alignItemsCenter>
              Secondary Categories
              <InfoTooltip>
                Choose the Secondary Categories that represent your product offerings. This helps
                retailers discover you based on what you sell.
              </InfoTooltip>
            </Flex>
          }
          id="secondary-categories"
        >
          <Controller
            control={control}
            name="secondaryCategories"
            render={({ field }) => (
              <CategorySelector
                value={(field.value ?? []).filter((v): v is string => typeof v === 'string')}
                onChange={(categories) => {
                  field.onChange(categories);
                  setMarketplaceData({ secondaryCategories: categories });
                }}
                label="Secondary Categories"
              />
            )}
          />
        </Form.Field>
        <div>
          <Flex justifyContent="space-between" alignItems="center">
            <Body variant="Body/Regular">NSFW Products</Body>
            <Controller
              control={control}
              name="nsfwProducts"
              render={({ field }) => (
                <Toggle
                  name="nsfwProducts"
                  checked={field.value || false}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    setMarketplaceData({ nsfwProducts: e.target.checked });
                  }}
                />
              )}
            />
          </Flex>
          <Body variant="Body/Body Small" color="bodyTextSecondary">
            Do you sell products that are Not Safe For Work?
          </Body>
        </div>
      </Form.FieldGroup>
      <Form.Footer column gap="16px">
        <PrimaryButton type="submit" disabled={updatingCompany.status === 'loading'}>
          {updatingCompany.status === 'loading' ? 'Saving...' : 'Next'}
        </PrimaryButton>
      </Form.Footer>
    </Form>
  );
};

export default BrandInfo;
